import React, { useEffect, useRef, useState, useMemo } from 'react';
import ReportingTable from './reporting-table';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Grid,
  Chip,
} from '@mui/material';
import { reportingApi } from 'src/api/reporting';
import moment from 'moment';
import { parseInt, uniqBy } from 'lodash';
import toast from 'react-hot-toast';
import SummaryTable from './summary/SummaryTable';
import HourComparisonChart from './summary/HourComparisonChart';
import AccountWiseHourComparisonChart from './summary/AccountWiseHourComparisonChart';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const columns = [
  {
    id: 'billableAccount',
    label: 'Account',
    width: '8%',
    maxWidth: '8%',
  },
  { id: 'project', label: 'Project', width: '5.5%', maxWidth: '5.5%' },
  { id: 'caseKey', label: 'Case Key', width: '6.3%', maxWidth: '6.3%' },
  { id: 'summary', label: 'Case Title', width: '39%', maxWidth: '39%' },
  { id: 'timeEstimate', label: 'Estimate', width: '6.3%', maxWidth: '6.3%' },
  {
    id: 'sprintAllocatedTime',
    label: 'Sprint Allocated',
    width: '6.3%',
    maxWidth: '6.3%',
  },
  { id: 'remainingTime', label: 'Remaining', width: '6.8%', maxWidth: '6.8%' },
  { id: 'loggedTime', label: 'Logged', width: '5.3%', maxWidth: '5.3%' },
  { id: 'category', label: 'Category', width: '6.1%', maxWidth: '6.1%' },
  { id: 'sprint', label: 'Sprint', width: '5.1%', maxWidth: '5.1%' },
  { id: 'releaseVersion', label: 'Version', width: '5.3%', maxWidth: '5.3%' },
];

const summaryColumns = [
  { id: 'accountName', label: 'Accounts', align: 'left' },
  {
    id: 'totalHoursAllocated',
    label: 'Hours Allocated',
    align: 'right',
    format: (value) => value.toFixed(2),
  },
  {
    id: 'totalHoursUtilized',
    label: 'Hours Utilized',
    align: 'right',
    format: (value) => value.toFixed(2),
  },
];

const Reporting = () => {
  const [reportingData, setReportingData] = useState([]);
  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem('reportingPage');
    return saved ? parseInt(saved) : 0;
  });
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('reportingPageSize');
    return saved ? parseInt(saved) : 25;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedOrganization, setSelectedOrganization] = useState(() => {
    const saved = localStorage.getItem('selectedOrganization');
    return saved ? JSON.parse(saved) : null;
  });
  const [organizations, setOrganizations] = useState([]); // Store the autocomplete options
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [sprints, setSprints] = useState([]); // Store fetched sprints
  const [allSprints, setAllSprints] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState(
    localStorage.getItem('selectedSprint')
      ? JSON.parse(localStorage.getItem('selectedSprint'))
      : null
  );
  const [loadingSprints, setLoadingSprints] = useState(false);
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true); // Add loading state for summary
  // const [selectedBillingAccount, setSelectedBillingAccount] = useState(() => {
  //   const saved = localStorage.getItem('selectedBillingAccount');
  //   return saved ? JSON.parse(saved) : { id: 'all', name: 'All' };
  // });
  const [selectedBillingAccounts, setSelectedBillingAccounts] = useState(() => {
    const saved = localStorage.getItem('selectedBillingAccounts');
    return saved ? JSON.parse(saved) : [];
  });

  // Refs for focus state
  const orgFocusRef = useRef(false);
  const sprintFocusRef = useRef(false);
  const billingAccountFocusRef = useRef(false);

  // Add new state for sorting
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem('reportingSortConfig');
    return saved
      ? JSON.parse(saved)
      : {
          column: 'billableAccount',
          sort: 1,
        };
  });

    // Add this helper function
    const getSprintIdsWithSameName = (_selectedSprint, _allSprints) => {
      if (!_selectedSprint || !_allSprints) return _selectedSprint?.id;

      const sameSprints = _allSprints.filter(
        sprint => sprint.name === _selectedSprint.name
      );

      return sameSprints.length > 0
        ? sameSprints.map(sprint => sprint.id).join(',')
        : _selectedSprint?.id;
    };

  // Add a mapping object for the column names
  const sortColumnMapping = {
    sprint: 'sprint.name',
    project: 'project.name',
    releaseVersion: 'releaseVersion.name',
    billableAccount: 'billableAccount.name',
  };

  useEffect(() => {
    if (
      !selectedOrganization?.id
      // !selectedSprint?.id
      // !billingAccounts?.length
    )
      return;

    const fetchData = async () => {
      try {
        const response = await reportingApi.getAllReportings({
          page,
          pageSize,
          billingAccountId:
          selectedBillingAccounts?.length > 0
            ? selectedBillingAccounts.map((account) => account.id).join(',')
            : billingAccounts
                .map((billingAccount) => billingAccount?.id)
                .join(','),
            // selectedBillingAccount?.id === 'all'
            //   ? billingAccounts
            //       ?.map((billingAccount) => billingAccount?.id)
            //       .join(',')
            //   : selectedBillingAccount?.id,
          from: moment(selectedSprint?.startDate).format('YYYY-MM-DD'),
          to: moment(selectedSprint?.endDate).format('YYYY-MM-DD'),
          organizationId: selectedOrganization?.id,
          sprintId: getSprintIdsWithSameName(selectedSprint, allSprints) || '',
          column: sortColumnMapping[sortConfig.column] || sortConfig.column, // Use mapping if exists, otherwise use original column
          sort: sortConfig.sort,
        });
        setReportingData(response?.data?.data || []);
        setTotalRecords(response?.data?.totalRecords || 0);
        setTotalPages(response?.data?.totalPages || 0);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    fetchData();
  }, [
    page,
    pageSize,
    selectedOrganization,
    selectedSprint,
    sortConfig,
    billingAccounts,
    // selectedBillingAccount,
    selectedBillingAccounts
  ]);

  // Fetch billing accounts for the autocomplete based on search term
  const fetchOrganizations = async () => {
    setLoadingAutocomplete(true);
    try {
      const response = await reportingApi.getOrganizations();
      setOrganizations(response?.data || []);

      // Get saved org ID from localStorage
      const savedOrg = localStorage.getItem('selectedOrganization');
      const savedOrgData = savedOrg ? JSON.parse(savedOrg) : null;

      // Find the matching organization with fresh data
      const orgToSelect = savedOrgData
        ? response.data.find((org) => org.id === savedOrgData.id)
        : response.data.find((org) => org.id === '64e4d2c228dea22ee9d5245f');

      // Set the organization with fresh data
      setSelectedOrganization(orgToSelect);
      if (orgToSelect) {
        localStorage.setItem(
          'selectedOrganization',
          JSON.stringify(orgToSelect)
        );
      }

      const billingResponse = await reportingApi.getBillingAccounts({
        orgId: orgToSelect ? orgToSelect.id : '64e4d2c228dea22ee9d5245f',
      });

      setBillingAccounts(billingResponse?.data || []);
    } catch (error) {
      console.error('Failed to fetch billing accounts:', error);
    }
    setLoadingAutocomplete(false);
  };
  // Fetch summary data for the selected sprint and organization
  const fetchSummaryData = async () => {
    if (
      !selectedOrganization?.id 
      // !billingAccounts?.length ||
      // !selectedSprint?.id
    ) {
      setLoadingSummary(false);
      return;
    }

    setLoadingSummary(true); // Set loading to true before fetching
    try {
      const response = await reportingApi.getSummaryData({
        sprintId: getSprintIdsWithSameName(selectedSprint, allSprints) || '',
        billingAccountId:
          selectedBillingAccounts?.length > 0
            ? selectedBillingAccounts.map((account) => account.id).join(',')
            : billingAccounts
                .map((billingAccount) => billingAccount?.id)
                .join(','),
        organizationId: selectedOrganization?.id,
        from: moment(selectedSprint?.startDate).format('YYYY-MM-DD'),
        to: moment(selectedSprint?.endDate).format('YYYY-MM-DD'),
      });
      setSummaryData(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch summary data:', error);
    }
    setLoadingSummary(false); // Set loading to false after fetching
  };
  // Fetch sprints for the selected organization based on sprint search term
  const fetchOrganizationSprints = async (organization) => {
    setLoadingSprints(true);
    try {
      const response = await reportingApi.getOrganizationsSprints({
        orgId: organization?.id,
      });
      const uniqueSprintNames = uniqBy(
        response.data,
        (item: any) => item?.name
      );
      setAllSprints(response?.data || []);
      setSprints(uniqueSprintNames || []);
      // Set the first sprint as default if available
      if (response?.data?.length === 0) {
        setSelectedSprint(null);
      }
      if (response?.data?.length > 0) {
        setSelectedSprint(response.data[0]); // Automatically set the first sprint as selected
      }
    } catch (error) {
      console.error('Failed to fetch organization sprints:', error);
    }
    setLoadingSprints(false);
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Fetch sprints when the selected organization changes
  useEffect(() => {
    if (selectedOrganization) {
      fetchOrganizationSprints(selectedOrganization); // Fetch sprints based on selected organization
    }
  }, [selectedOrganization]);

  useEffect(() => {
    fetchSummaryData();
  }, [
    selectedSprint,
    selectedOrganization,
    billingAccounts,
    selectedBillingAccounts,
  ]);

  // Set the default value on the first load
  useEffect(() => {
    const savedOrg = localStorage.getItem('selectedOrganization');
    if (savedOrg) {
      setSelectedOrganization(JSON.parse(savedOrg));
    } else {
      const defaultOrg = organizations.find(
        (org) => org.id === '64e4d2c228dea22ee9d5245f'
      );
      setSelectedOrganization(defaultOrg);
      if (defaultOrg) {
        localStorage.setItem(
          'selectedOrganization',
          JSON.stringify(defaultOrg)
        );
      }
    }
  }, [organizations]);

  const handleSelectedRows = (selectedData) => {
    setSelectedRowsData(selectedData);
  };

  const exportHandler = async () => {
    try {
      if (
        // !selectedSprint?.id ||
        !selectedOrganization?.id
        // !billingAccounts?.length
      ) {
        toast.error('No data to export');
        return;
      }

      const blob = await reportingApi.billableStatisticsExport({
        billingAccountIds:
          selectedBillingAccounts?.length > 0
            ? selectedBillingAccounts.map((account) => account.id).join(',')
            : billingAccounts
                ?.map((billingAccount) => billingAccount?.id)
                .join(','),
        from: moment(selectedSprint?.startDate).format('YYYY-MM-DD'),
        to: moment(selectedSprint?.endDate).format('YYYY-MM-DD'),
        sprintId: getSprintIdsWithSameName(selectedSprint, allSprints) || '',
        organizationId: selectedOrganization?.id,
      });

      // Create a link to download the blob
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${selectedSprint?.name} ${moment(new Date()).format(
          'YYYY-MM-DD'
        )}.xlsx`
      ); // Adjust the filename and extension if needed
      document.body.appendChild(link);
      link.click();

      // Clean up and remove the link
      link.parentNode?.removeChild(link);
      toast.success('Reporting Sprints Export Successfully');
      setSelectedRowsData([]);
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Failed to export', error);
    }
  };

  // Add handler for sorting
  const handleSort = (columnId) => {
    const newSortConfig = {
      column: columnId,
      sort: sortConfig.column === columnId ? sortConfig.sort * -1 : 1,
    };
    setSortConfig(newSortConfig);
    localStorage.setItem('reportingSortConfig', JSON.stringify(newSortConfig));
  };

  const handleOrganizationChange = async (event, newValue) => {
    setSelectedSprint(null);
    localStorage.setItem('selectedOrganization', JSON.stringify(newValue));
    setSelectedOrganization(newValue);

    // Reset pagination
    setPage(0);
    localStorage.setItem('reportingPage', '0');
    setPageSize(25);
    localStorage.setItem('reportingPageSize', '25');

    // Reset billing account selection to "All"
    setSelectedBillingAccounts([]);
    localStorage.setItem('selectedBillingAccounts', JSON.stringify([]));

    // Fetch billing accounts for the new organization
    if (newValue) {
      try {
        const billingResponse = await reportingApi.getBillingAccounts({
          orgId: newValue.id,
        });
        setBillingAccounts(billingResponse?.data || []);
      } catch (error) {
        console.error('Failed to fetch billing accounts:', error);
        setBillingAccounts([]);
      }
    } else {
      setBillingAccounts([]);
    }
  };

  const handleSprintChange = (event, newValue) => {
    setSelectedSprint(newValue);
    localStorage.setItem('selectedSprint', JSON.stringify(newValue));
    setPage(0);
    localStorage.setItem('reportingPage', '0');
    setPageSize(25);
    localStorage.setItem('reportingPageSize', '25');
  };

  const handleReset = async () => {
    try {
      // Find the default organization
      const defaultOrg = organizations.find(
        (org) => org.id === '64e4d2c228dea22ee9d5245f'
      );

      // Reset organization to default
      setSelectedOrganization(defaultOrg);
      localStorage.setItem('selectedOrganization', JSON.stringify(defaultOrg));

      // Fetch sprints for default organization
      const response = await reportingApi.getOrganizationsSprints({
        orgId: defaultOrg?.id,
      });

      // Set default sprint (first sprint in the list)
      if (response?.data?.length > 0) {
        const defaultSprint = response.data[0];
        setSelectedSprint(defaultSprint);
        localStorage.setItem('selectedSprint', JSON.stringify(defaultSprint));
      } else {
        setSelectedSprint(null);
        localStorage.removeItem('selectedSprint');
      }

      // Fetch billing accounts for the default organization
      const billingResponse = await reportingApi.getBillingAccounts({
        orgId: defaultOrg?.id,
      });
      setBillingAccounts(billingResponse?.data || []);

      // Reset pagination and sorting
      setPage(0);
      setPageSize(25);
      setSortConfig({ column: 'billableAccount', sort: 1 });
      localStorage.setItem('reportingPage', '0');
      localStorage.setItem('reportingPageSize', '25');
      localStorage.setItem(
        'reportingSortConfig',
        JSON.stringify({
          column: 'billableAccount',
          sort: 1,
        })
      );

      // Reset billing account to "All"
      setSelectedBillingAccounts([]);
      localStorage.setItem('selectedBillingAccounts', JSON.stringify([]));
    } catch (error) {
      console.error('Failed to reset to defaults:', error);
      toast.error('Failed to reset to defaults');
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    localStorage.setItem('reportingPage', newPage.toString());
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    localStorage.setItem('reportingPageSize', newPageSize.toString());
  };

  // Update the handleBillingAccountChange function
  const handleBillingAccountChange = (event, newValue) => {
    if (!newValue) return;

    // If "All" is selected, clear other selections
    if (Array.isArray(newValue)) {
      // Handle multiple selections
      const filteredValues = newValue.filter((value) => value.id !== 'all');
      setSelectedBillingAccounts(filteredValues);
      localStorage.setItem(
        'selectedBillingAccounts',
        JSON.stringify(filteredValues)
      );
    }
    setPage(0);
    localStorage.setItem('reportingPage', '0');
  };

  // Add handler to remove billing account
  const handleRemoveBillingAccount = (accountId) => {
    const newAccounts = selectedBillingAccounts.filter(
      (account) => account.id !== accountId
    );
    setSelectedBillingAccounts(newAccounts);
    localStorage.setItem(
      'selectedBillingAccounts',
      JSON.stringify(newAccounts)
    );
  };

  const sortedSprints = useMemo(
    () =>
      [...sprints].sort(
        (a, b) =>
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      ),
    [sprints]
  );
  return (
    <Box p={2}>
      <Typography color="textPrimary" variant="h3">
        Reporting
      </Typography>

      <Box
        display="flex"
        width="100%"
        mb={2}
        mt={2}
        justifyContent="space-between"
        alignItems="center">
        <Box width="70%" display="flex" gap={2} flexDirection="column">
          <Box display="flex" gap={2}>
            <Autocomplete
              id="organization-autocomplete"
              options={organizations}
              getOptionLabel={(option) => option.name || ''}
              loading={loadingAutocomplete}
              value={selectedOrganization} // Controlled by state
              isOptionEqualToValue={(option, value) => option.id === value?.id} // Fix the comparison issue
              clearIcon={null}
              sx={{
                width: '30%',
              }}
              onChange={handleOrganizationChange}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  label="Organization"
                  variant="outlined"
                  InputLabelProps={{
                    sx: {
                      top: (() => {
                        if (selectedOrganization) {
                          return '0px';
                        }
                        if (!orgFocusRef.current) {
                          return '-7px';
                        }
                        return '0px';
                      })(),
                    },
                  }}
                  onFocus={() => {
                    orgFocusRef.current = true;
                  }}
                  onBlur={() => {
                    orgFocusRef.current = false;
                  }}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      height: '40px',
                    },
                    endAdornment: (
                      <>
                        {loadingAutocomplete ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Billing Account Autocomplete */}
            {selectedOrganization && (
              <Autocomplete
                id="billing-account-autocomplete"
                multiple
                options={billingAccounts}
                limitTags={1}
                getOptionLabel={(option) => option?.name || ''}
                value={selectedBillingAccounts}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                clearIcon={null}
                sx={{
                  width: '30%',
                  '& .MuiOutlinedInput-root': {
                    minHeight: '40px',
                    height: 'auto',
                    padding: '3px 9px',
                  },
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                      sx={{
                        height: 24,
                        '& .MuiChip-label': {
                          fontSize: '0.75rem',
                          paddingRight: '4px',
                        },
                      }}
                    />
                  ))
                }
                onChange={handleBillingAccountChange}
                onFocus={() => {
                  billingAccountFocusRef.current = true;
                }}
                onBlur={() => {
                  billingAccountFocusRef.current = false;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Billing Account"
                    variant="outlined"
                    InputLabelProps={{
                      sx: {
                        top: (() => {
                          if (selectedBillingAccounts.length > 0) {
                            return '0px';
                          }
                          if (!billingAccountFocusRef.current) {
                            return '-7px';
                          }
                          return '0px';
                        })(),
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        height: 'auto',
                        minHeight: '40px',
                        padding: '0 !important',
                      },
                    }}
                  />
                )}
              />
            )}

            {/* Autocomplete for selecting sprint */}
            {selectedOrganization && (
              <Autocomplete
                id="sprint-autocomplete"
                options={sortedSprints}
                getOptionLabel={(option) =>
                  `${option.name}${
                    option === sortedSprints[0] ? ' (Current Sprint)' : ''
                  }`
                }
                loading={loadingSprints}
                value={selectedSprint}
                defaultValue={sprints?.[0]}
                sx={{ width: '30%' }}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                onChange={handleSprintChange}
                clearIcon={null}
                renderOption={(props, option) => {
                  const now = new Date();
                  const startDate = new Date(option.startDate);
                  const endDate = new Date(option.endDate);
                  const isCurrent = now >= startDate && now <= endDate;

                  return (
                    <li
                      {...props}
                      style={{
                        fontWeight:
                          option === sortedSprints[0] ? 'bold' : 'normal',
                      }}>
                      {option.name}
                      {option === sortedSprints[0] ? ' (Current sprint)' : ''}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sprint"
                    variant="outlined"
                    onFocus={() => {
                      sprintFocusRef.current = true;
                    }}
                    onBlur={() => {
                      sprintFocusRef.current = false;
                    }}
                    InputLabelProps={{
                      sx: {
                        top: (() => {
                          if (selectedSprint) {
                            return '0px';
                          }
                          if (!sprintFocusRef.current) {
                            return '-7px';
                          }
                          return '0px';
                        })(),
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        height: '40px',
                      },
                      endAdornment: (
                        <>
                          {loadingSprints ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Box>

          {/* Billing Account Chips */}
          {selectedBillingAccounts.length > 0 && (
            <Box display="flex" gap={1} flexWrap="wrap">
              {selectedBillingAccounts.map((account) => (
                <Box
                  key={account.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'background.paper',
                    borderRadius: '16px',
                    padding: '4px 8px',
                    gap: '4px',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}>
                  <Typography variant="body2">{account.name}</Typography>
                  <Box
                    component="span"
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => handleRemoveBillingAccount(account.id)}>
                    ×
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleReset}
            title="Reset to defaults"
            sx={{
              minWidth: '50px',
              p: 0,
            }}>
            <RestartAltIcon />
          </Button>

          <Button variant="contained" color="secondary" onClick={exportHandler}>
            Export
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
        {/* All three components in a single row */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              height: '350px',
              position: 'relative',
              bgcolor: 'background.paper',
              borderRadius: 1,
              overflow: 'hidden',
            }}>
            <SummaryTable
              data={summaryData}
              columns={summaryColumns}
              loading={loadingSummary}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              height: '350px',
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}>
            <HourComparisonChart
              selectedOrganization={selectedOrganization}
              billingAccountId={
                selectedBillingAccounts?.length > 0
                  ? selectedBillingAccounts
                  : null
              }
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              height: '350px',
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}>
            <AccountWiseHourComparisonChart
              selectedOrganization={selectedOrganization}
              billingAccountId={
                selectedBillingAccounts?.length > 0
                  ? selectedBillingAccounts
                  : null
              }
            />
          </Box>
        </Grid>
      </Grid>

      <ReportingTable
        columns={columns}
        rows={reportingData}
        page={page}
        setPage={handlePageChange}
        pageSize={pageSize}
        setPageSize={handlePageSizeChange}
        totalRecords={totalRecords}
        totalPages={totalPages}
        onSelectionChange={handleSelectedRows}
        selectedRows={selectedRowsData}
        setSelectedRows={setSelectedRowsData}
        onSort={handleSort}
        sortConfig={sortConfig}
      />
    </Box>
  );
};

export default Reporting;
